import React, {memo, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import cn from 'classnames';
import {RouteName} from '@/app/constants/routing';
import {Interpolation, useLocalizer} from '@/common/services/localizer';
import {useRouter} from '@/common/services/router';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {StoreThunkDispatch} from '@/app/store';
import {selectDomainConfig} from '@/app/store/config/selectors';
import {determinateCoordinates, determinateExactSettlement} from '@/app/store/geolocation/actions';
import {ContainedButton} from '@/common/components/ContainedButton';
import styles from './GeoAccessRequestDialog.css';

// TODO to config
const ignoreRequestAfterDenyTime = 1000 * 60 * 60 * 24 * 30; // 30 days

interface GeoAccessRequestDialogProps {
	className?: string;
	onClose: () => void;
}

export const GeoAccessRequestDialog = memo((props: GeoAccessRequestDialogProps) => {
	const {className, onClose} = props;
	const t = useLocalizer();
	const {language} = t;

	const {domainName} = useSelector(selectDomainConfig);

	const [determining, setDetermining] = useState(false);

	const {history} = useRouter();
	const urlBuilder = useUrlBuilder();
	const dispatch = useDispatch<StoreThunkDispatch>();

	const handleDeny = useCallback(() => {
		onClose();

		// Remember deny response and not retry a certain amount of time
		const ignoreUntil = Date.now() + ignoreRequestAfterDenyTime;
		localStorage.setItem('sinoptik:ignoreGeolocationRequestUntil', ignoreUntil.toString());
	}, []);

	const handleAllow = useCallback(async () => {
		setDetermining(true);

		let determinateResult;
		try {
			await dispatch(determinateCoordinates({timeout: 15000})); // TODO test this timeout
			determinateResult = await dispatch(determinateExactSettlement({language}));
		} catch (error) {
			if (error instanceof GeolocationPositionError) {
				if (error.code === error.PERMISSION_DENIED || error.code === error.POSITION_UNAVAILABLE) {
					handleDeny();
					return;
				}
			}

			throw error;
		} finally {
			setDetermining(false);
		}

		if (determinateResult.payload) {
			const location = determinateResult.payload.location;

			// TODO push only if determined location mismatch with the current location
			history!.push(urlBuilder(RouteName.weather, {location}));

			// TODO handle deny by user and separate user deny and system errors
			onClose();
		}

		setDetermining(false);
	}, [language]);

	return (
		<div className={cn(styles.root, className)}>
			<button className={styles.closeButton} disabled={determining} type='button' onClick={onClose} />
			<p className={styles.text}>
				<Interpolation l10nCode='geoAccessRequire' domainName={() => <strong>{domainName}</strong>} />
			</p>
			<div className={styles.controls}>
				<ContainedButton
					className={styles.actionButton}
					disabled={determining}
					loading={determining}
					sizeType='large'
					onClick={handleAllow}>
					{t('geoAccessAllow')}
				</ContainedButton>
				<ContainedButton
					className={styles.actionButton}
					disabled={determining}
					styleType='secondary'
					sizeType='large'
					onClick={handleDeny}>
					{t('geoAccessDeny')}
				</ContainedButton>
			</div>
		</div>
	);
});

GeoAccessRequestDialog.displayName = 'GeoAccessRequestDialog';
